import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Login from "./pages/Login";
import DashboardLayout from "./components/layouts/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import PrivateRoute from "./routes/PrivateRoute";
import PublicRoute from "./routes/PublicRoute";
import RoleProtectedRoute from "./routes/RoleProtectedRoute";
import MyProfile from "./pages/users/MyProfile";
import ScrollToTop from "./utils/ScrollToTopUtils";

import { NotificationProvider } from "./context/NotificationContext";
import { LoaderProvider } from "./utils/LoaderUtils";
import ChangePassword from "./pages/users/ChangePassword";
import StoreList from "./pages/stores/StoreList";
import StoreAdd from "./pages/stores/StoreAdd";
import StoreEdit from "./pages/stores/StoreEdit";
import SocialMediaSettings from "./pages/stores/tabs/SocialMediaSettings";
import PaymentGatewaySettings from "./pages/stores/tabs/PaymentGatewaySettings";
import EditGeneralSettings from "./pages/stores/tabs/EditGeneralSettings";
import GeneralSettings from "./pages/stores/tabs/GeneralSettings";
import WarehouseSettings from "./pages/stores/tabs/WarehouseSettings";

import StoreAdminList from "./pages/stores/admin/List";
import StoreAdminAddNew from "./pages/stores/admin/AddNew";
import StoreAdminEdit from "./pages/stores/admin/Edit";
import StoreAdminChangePassword from "./pages/stores/admin/ChangePassword";

import UserList from "./pages/stores/users/List";
import UserAddNew from "./pages/stores/users/AddNew";
import UserEdit from "./pages/stores/users/Edit";
import UserChangePassword from "./pages/stores/users/ChangePassword";

import BannerList from "./pages/banners/List";
import BannerAddNew from "./pages/banners/AddNew";
import BannerEdit from "./pages/banners/Edit";

import TestimonialsList from "./pages/testimonials/List";
import TestimonialsAddNew from "./pages/testimonials/AddNew";
import TestimonialsEdit from "./pages/testimonials/Edit";

import CmsPagesDetails from "./pages/cmsPages/AddNew";

import CategoryList from "./pages/category/List";
import CategoryAddNew from "./pages/category/AddNew";
import CategoryEdit from "./pages/category/Edit";

import BrandList from "./pages/brand/List";
import BrandAddNew from "./pages/brand/AddNew";
import BrandEdit from "./pages/brand/Edit";

import CouponList from "./pages/promo/coupon/List";
import CouponAddNew from "./pages/promo/coupon/AddNew";
import CouponEdit from "./pages/promo/coupon/Edit";

import DealOfTheDayList from "./pages/promo/dealoftheday/List";
import DealOfTheDayAddNew from "./pages/promo/dealoftheday/AddNew";
import DealOfTheDayEdit from "./pages/promo/dealoftheday/Edit";

import DiscountList from "./pages/promo/discount/List";
import DiscountAddNew from "./pages/promo/discount/AddNew";
import DiscountEdit from "./pages/promo/discount/Edit";

import BlogList from "./pages/blogs/List";
import BlogAddNew from "./pages/blogs/AddNew";
import BlogEdit from "./pages/blogs/Edit";

import SupplierList from "./pages/supplier/List";
import SupplierAddNew from "./pages/supplier/AddNew";
import SupplierEdit from "./pages/supplier/Edit";

import FormList from "./pages/form/List";
import FormAddNew from "./pages/form/AddNew";
import FormEdit from "./pages/form/Edit";
import FormSubmission from "./pages/form/Submission";
import FormSubmissionDetail from "./pages/form/SubmissionDetail";

import ShippingList from "./pages/shipping/List";

import OrderList from "./pages/orders/List";
import OrderDetail from "./pages/orders/Detail";

import WishList from "./pages/wishlist/List";
import WishlistUser from "./pages/wishlist/User";

import CustomerList from "./pages/customer/List";
import CustomerOrderList from "./pages/customer/Order";

import Sitemap from "./pages/sitemap/List";
import SitemapAddNew from "./pages/sitemap/AddNew";

import ProductList from "./pages/product/List";
import ProductAddNew from "./pages/product/AddNew";
import ForgotPassword from "./pages/ForgotPassword";
import ShippingSettings from "./pages/stores/tabs/ShippingSettings";
import EmailSettings from "./pages/stores/tabs/EmailSettings";
import ResetPasswordForm from "./pages/ResetPasswordForm";
import { AuthProvider } from "./context/AuthContext";
import PageNotFound from "./pages/PageNotFound";

import PermissionsAdd from "./pages/stores/permissions/Add";

import HealthCheck from "./HealthCheck";
import Unauthorized from "./pages/Unauthorized";
import Invoice from "./pages/orders/invoice/Invoice";
import ShippingLabel from "./pages/orders/invoice/ShippingLabel";
import Queries from "./pages/customer/Queries";
import Gallery from "./pages/gallery/List";
import PosList from "./pages/pos/List";

import StockHistoryPage from "./pages/inventory/StockHistoryPage";
import InventoryListPage from "./pages/inventory/InventoryListPage";

import PurchaseInvoiceListPage from "./pages/inventory/pi/PurchaseInvoiceListPage";
import PurchaseInvoiceAddNew from "./pages/inventory/pi/AddNew";
import PolicyManagement from "./pages/cmsPages/PolicyManagement";
import SmsSettings from "./pages/stores/tabs/SmsSettings";
import SmsTemplateSettings from "./pages/stores/tabs/SmsTemplateSettings";
import OfflineSales from "./pages/pos/OfflineSales";
import OfflineInvoice from "./pages/pos/Invoice";

function App() {
  return (
    <LoaderProvider>
      <Router>
        <ScrollToTop />

        <Routes>
          {/* ✅ Public Routes — NO AuthProvider here */}
          <Route element={<PublicRoute />}>
            <Route path="/" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPasswordForm />} />

            <Route path="/health-check" element={<HealthCheck />} />
          </Route>

          {/* ✅ Private Routes — Wrap inside AuthProvider */}
          <Route
            element={
              <AuthProvider>
                <NotificationProvider>
                  <PrivateRoute />
                </NotificationProvider>
              </AuthProvider>
            }
          >
            <Route path="/pos">
              <Route index element={<PosList />} />
              <Route path="offline-sales" element={<OfflineSales />} />
              <Route path="offline-sales/:id" element={<OfflineInvoice />} />
            </Route>
            
            <Route path="/" element={<DashboardLayout />}>
              <Route element={<RoleProtectedRoute />}>
                <Route path="dashboard" element={<Dashboard />} />

                <Route path="sitemap" element={<Sitemap />} />
                <Route path="queries" element={<Queries />} />

                <Route path="gallery" element={<Gallery />} />

                <Route path="sitemap">
                  <Route path="" element={<Sitemap />} />
                  <Route path="add-new" element={<SitemapAddNew />} />
                </Route>

                {/* wishlist Routes */}
                <Route path="wishlist">
                  <Route path="" element={<WishList />} />
                  <Route path="user/:id" element={<WishlistUser />} />
                </Route>

                {/* User Routes */}
                <Route path="user">
                  <Route path="my-profile" element={<MyProfile />} />
                  <Route path="change-password" element={<ChangePassword />} />
                </Route>

                {/* order related paths */}
                <Route path="orders">
                  <Route path="details/:id" element={<OrderDetail />} />
                  <Route path="invoice/:id" element={<Invoice />} />
                  <Route path="label/:id" element={<ShippingLabel />} />
                  <Route path=":id" element={<OrderList />} />
                </Route>

                <Route path="customer">
                  <Route path="all" element={<CustomerList />} />
                  <Route path="details/:id" element={<CustomerOrderList />} />
                </Route>

                <Route path="shipments">
                  <Route path=":id" element={<ShippingList />} />
                </Route>

                {/* promotions ke sabhi path yaha define honge  */}
                <Route path="coupon">
                  <Route index element={<CouponList />} />
                  <Route path="add-new" element={<CouponAddNew />} />
                  <Route path="edit/:id" element={<CouponEdit />} />
                </Route>

                <Route path="deal-of-the-day">
                  <Route index element={<DealOfTheDayList />} />
                  <Route path="add-new" element={<DealOfTheDayAddNew />} />
                  <Route path="edit/:id" element={<DealOfTheDayEdit />} />
                </Route>

                <Route path="discount">
                  <Route index element={<DiscountList />} />
                  <Route path="add-new" element={<DiscountAddNew />} />
                  <Route path="edit/:id" element={<DiscountEdit />} />
                </Route>

                {/* banners ke sabhi path yaha define honge  */}
                <Route path="banners">
                  <Route index element={<BannerList />} />
                  <Route path="add-new" element={<BannerAddNew />} />
                  <Route path="edit/:id" element={<BannerEdit />} />
                </Route>

                <Route path="testimonials">
                  <Route index element={<TestimonialsList />} />
                  <Route path="add-new" element={<TestimonialsAddNew />} />
                  <Route path="edit/:id" element={<TestimonialsEdit />} />
                </Route>

                <Route path="blog">
                  <Route index element={<BlogList />} />
                  <Route path="add-new" element={<BlogAddNew />} />
                  <Route path="edit/:id" element={<BlogEdit />} />
                </Route>

                <Route path="policies-management" element={<PolicyManagement />} />
                <Route path="cms/:id" element={<CmsPagesDetails />} />

                {/* inventory management paths */}
                <Route path="category">
                  <Route index element={<CategoryList />} />
                  <Route path="add-new" element={<CategoryAddNew />} />
                  <Route path="edit/:id" element={<CategoryEdit />} />
                </Route>

                <Route path="brand">
                  <Route index element={<BrandList />} />
                  <Route path="add-new" element={<BrandAddNew />} />
                  <Route path="edit/:id" element={<BrandEdit />} />
                </Route>

                <Route path="product">
                  <Route index element={<ProductList />} />
                  <Route path="add-new" element={<ProductAddNew />} />
                </Route>

                <Route path="form">
                  <Route index element={<FormList />} />
                  <Route path="add-new" element={<FormAddNew />} />
                  <Route path="edit/:id" element={<FormEdit />} />
                </Route>

                <Route path="submissions">
                  <Route path=":formId?" element={<FormSubmission />} />
                  <Route path=":formId/:id" element={<FormSubmissionDetail />} />
                </Route>
                
                {/* inventory related paths */}
                <Route path="suppliers">
                  <Route index element={<SupplierList />} />
                  <Route path="add-new" element={<SupplierAddNew />} />
                  <Route path="edit/:id" element={<SupplierEdit />} />
                </Route>
                
                <Route path="purchase-invoices">
                  <Route index element={<PurchaseInvoiceListPage />} />
                  <Route path="add-new" element={<PurchaseInvoiceAddNew />} />
                  <Route path="edit/:id" element={<PurchaseInvoiceAddNew />} />
                </Route>

                <Route path="stock-history" element={<StockHistoryPage />} />
                <Route path="inventory-list" element={<InventoryListPage />} />

                {/* Stores ke sabhi path yaha define honge  */}
                <Route path="users">
                  <Route index element={<UserList />} />
                  <Route path="add-new" element={<UserAddNew />} />
                  <Route path="edit/:id" element={<UserEdit />} />
                  <Route
                    path="change-password/:id"
                    element={<UserChangePassword />}
                  />
                  <Route
                    path="permissions/:id"
                    element={<PermissionsAdd />}
                  />
                </Route>

                <Route path="stores">
                  <Route path="list" element={<StoreList />} />
                  <Route path="add-new/" element={<StoreAdd />}>
                    <Route path="general" element={<GeneralSettings />} />
                  </Route>
                  <Route path="edit/:id" element={<StoreEdit />}>
                    <Route path="general" element={<EditGeneralSettings />} />
                    <Route path="social-media" element={<SocialMediaSettings />}/>
                    <Route path="payment-gateway" element={<PaymentGatewaySettings />}/>
                    <Route path="warehouse-settings" element={<WarehouseSettings />}/>
                    <Route path="shipping-settings" element={<ShippingSettings />}/>
                    <Route path="email-settings" element={<EmailSettings />} />
                    <Route path="sms-settings" element={<SmsSettings />} />
                    <Route path="sms-templates" element={<SmsTemplateSettings />} />
                  </Route>

                  <Route path="admin">
                    <Route path="list" element={<StoreAdminList />} />
                    <Route path="add-new" element={<StoreAdminAddNew />} />
                    <Route path="edit/:id" element={<StoreAdminEdit />} />
                    <Route
                      path="change-password/:id"
                      element={<StoreAdminChangePassword />}
                    />
                    <Route
                      path="permissions/:id"
                      element={<PermissionsAdd />}
                    />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<PageNotFound />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
        </Routes>
      </Router>

      <ToastContainer />
    </LoaderProvider>
  );
}

export default App;
